'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Blog } from '../components/types';
import { topicById, topicsForBlogs } from '../data/topics';
import { track } from '@lib/track';

const ALL = 'all';

// Reading time estimator
function readingTime(blog: Blog): number {
  const text = blog.content
    .map((s) => (typeof s.value === 'string' ? s.value : (s.value as { text?: string })?.text ?? ''))
    .join(' ');
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

export default function BlogIndexClient({ blogs, initialTopic }: { blogs: Blog[]; initialTopic?: string }) {
  const sorted = useMemo(
    () => [...blogs].sort((a, b) => new Date(b.lastmod).getTime() - new Date(a.lastmod).getTime()),
    [blogs]
  );

  // Pills are derived from the posts, so a topic with nothing written yet never
  // renders as a dead filter. Niki's outstanding topics appear here on their own
  // once the first post lands.
  const topics = useMemo(() => topicsForBlogs(sorted), [sorted]);

  const [activeTopic, setActiveTopic] = useState(() =>
    initialTopic && topics.some((t) => t.id === initialTopic) ? initialTopic : ALL
  );
  const [search, setSearch] = useState('');

  const enriched = useMemo(
    () => sorted.map((b) => ({ ...b, topic: topicById(b.category), mins: readingTime(b as Blog) })),
    [sorted]
  );

  const featured = enriched[0];
  const rest = enriched.slice(1);

  const filtered = useMemo(() => {
    return rest.filter((b) => {
      const matchTopic = activeTopic === ALL || b.topic.id === activeTopic;
      const q = search.toLowerCase();
      const matchSearch = !q || b.title.toLowerCase().includes(q) || (b.excerpt ?? '').toLowerCase().includes(q);
      return matchTopic && matchSearch;
    });
  }, [rest, activeTopic, search]);

  // A ?topic= deep link filters without anyone clicking a pill, so it needs its
  // own event — otherwise that arrival path is invisible and blog_topic_filter
  // reads as "nobody filters" when in fact they arrived pre-filtered.
  const landingTracked = useRef(false);
  useEffect(() => {
    if (landingTracked.current || activeTopic === ALL) return;
    landingTracked.current = true;
    track(
      'blog_topic_landing',
      { label: activeTopic, component: 'BlogIndex' },
      { event: 'blog_topic_landing', label: activeTopic, component: 'BlogIndex' }
    );
    // Deliberately mount-only: this measures how the page was entered, not every
    // subsequent pill click (that is blog_topic_filter's job).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the filtered view shareable and linkable from the article pages.
  // replaceState rather than a router push: no server round trip, no scroll jump.
  function selectTopic(id: string) {
    setActiveTopic(id);
    track(
      'blog_topic_filter',
      { label: id, component: 'BlogIndex' },
      { event: 'blog_topic_filter', label: id, component: 'BlogIndex' }
    );
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (id === ALL) url.searchParams.delete('topic');
    else url.searchParams.set('topic', id);
    window.history.replaceState(null, '', url.toString());
  }

  return (
    <main style={{ fontFamily: "var(--font-cormorant)", background: 'var(--bg-surface)', minHeight: '100vh', color: 'var(--text-on-light)' }}>

      <style>{`
        .blog-wrap * { box-sizing: border-box; }

        .blog-hero-card {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          background: var(--bg-surface);
          border-radius: 20px;
          overflow: hidden;
          color: var(--text-on-light);
          text-decoration: none;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .blog-hero-card:hover { transform: translateY(-4px); box-shadow: 0 24px 60px color-mix(in srgb, var(--ink) 25%, transparent); }
        @media (max-width: 700px) {
          .blog-hero-card { grid-template-columns: 1fr; }
        }

        .cat-pill {
          font-family: var(--font-dm-sans);
          font-size: 11px;
          font-weight: 500;
          padding: 5px 14px;
          border-radius: 20px;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          letter-spacing: 0.04em;
        }
        .cat-pill:hover { opacity: 0.85; transform: translateY(-1px); }

        .blog-card {
          background: var(--bg-surface);
          border-radius: 16px;
          overflow: hidden;
          color: var(--text-on-light);
          text-decoration: none;
          display: flex;
          flex-direction: column;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .blog-card:hover { transform: translateY(-5px); box-shadow: 0 20px 50px color-mix(in srgb, var(--ink) 30%, transparent); }
        .blog-card:hover .blog-card-img { transform: scale(1.05); }

        .blog-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .search-input {
          font-family: var(--font-dm-sans);
          font-size: 14px;
          background: var(--bg-card);
          border: 1px solid var(--border-on-light);
          border-radius: 10px;
          padding: 10px 16px;
          color: var(--text-on-light);
          outline: none;
          width: 240px;
          transition: border-color 0.2s, background 0.2s;
        }
        .search-input::placeholder { color: var(--text-on-light-faint); }
        .search-input:focus { border-color: var(--text-brand); background: var(--bg-card); }

        .tag-badge {
          font-family: var(--font-dm-sans);
          font-size: 10px;
          font-weight: 500;
          padding: 3px 10px;
          border-radius: 20px;
          letter-spacing: 0.04em;
          display: inline-block;
        }
        .read-meta {
          font-family: var(--font-dm-sans);
          font-size: 12px;
          opacity: 0.5;
        }
        .empty-state {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px 20px;
          opacity: 0.4;
          font-size: 18px;
          font-style: italic;
        }
      `}</style>

      <div className="blog-wrap" style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 20px 80px' }}>

        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: 52 }}>
          <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.5, marginBottom: 12 }}>
            Niki Charnock · Naturopathic Nutrition
          </p>
          <h1 style={{ fontSize: 'clamp(42px, 7vw, 80px)', fontWeight: 300, lineHeight: 1.05, letterSpacing: '-0.01em', margin: '0 0 16px' }}>
            The&nbsp;<em style={{ fontStyle: 'italic', fontWeight: 300 }}>Wellness</em> Blog
          </h1>
          <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 15, opacity: 0.6, maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
            Evidence-led insight on gut health, nutrition, mental wellbeing and natural living.
          </p>
        </header>

        {/* Featured post */}
        {featured && (
          <Link href={`/blog/${featured.slug}`} className="blog-hero-card" style={{ marginBottom: 52, display: 'grid' }}>
            <div style={{ padding: '40px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <span style={{ fontFamily: "var(--font-dm-sans)", fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.45 }}>Latest</span>
                <span style={{ fontFamily: "var(--font-dm-sans)", fontSize: 10, opacity: 0.3 }}>·</span>
                <span className="tag-badge" style={{ background: featured.topic.bg, color: featured.topic.fg }}>
                  {featured.topic.label}
                </span>
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 34px)', fontWeight: 400, lineHeight: 1.2, margin: '0 0 16px', color: 'var(--text-on-light)' }}>
                {featured.title}
              </h2>
              <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 14, lineHeight: 1.65, opacity: 0.65, margin: '0 0 28px', color: 'var(--text-on-light)' }}>
                {featured.excerpt}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontFamily: "var(--font-dm-sans)", fontSize: 13, fontWeight: 500, background: 'var(--text-brand)', color: 'var(--text-on-dark)', padding: '9px 22px', borderRadius: 8 }}>
                  Read article
                </span>
                <span className="read-meta" style={{ color: 'var(--text-on-light)' }}>{featured.mins} min read · By Niki Charnock</span>
              </div>
            </div>
            <div style={{ position: 'relative', minHeight: 320, background: 'var(--cream-dk)' }}>
              {featured.image && (
                <Image
                  src={featured.image}
                  alt={featured.title}
                  fill
                  sizes="(max-width: 700px) 100vw, 550px"
                  style={{ objectFit: 'contain' }}
                  priority
                />
              )}
            </div>
          </Link>
        )}

        {/* Filter + search bar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 32, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[{ id: ALL, label: 'All' }, ...topics].map((t) => (
              <button
                key={t.id}
                className="cat-pill"
                onClick={() => selectTopic(t.id)}
                aria-pressed={activeTopic === t.id}
                style={{
                  background: activeTopic === t.id ? 'var(--text-brand)' : 'var(--bg-card)',
                  color: activeTopic === t.id ? 'var(--text-on-dark)' : 'var(--text-on-light-muted)',
                  border: activeTopic === t.id ? 'none' : '1px solid var(--border-on-light)',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            className="search-input"
            type="text"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search articles"
          />
        </div>

        {/* Blog grid */}
        <div className="blog-grid">
          {filtered.length === 0 ? (
            <p className="empty-state">No articles found — try a different filter.</p>
          ) : (
            filtered.map((blog) => (
              <Link key={blog.id} href={`/blog/${blog.slug}`} className="blog-card">
                <div style={{ position: 'relative', paddingTop: '58%', overflow: 'hidden' }}>
                  {blog.image && (
                    <Image
                      src={blog.image}
                      alt={blog.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      style={{ objectFit: 'cover', transition: 'transform 0.4s ease' }}
                      className="blog-card-img"
                    />
                  )}
                </div>
                <div style={{ padding: '20px 22px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span className="tag-badge" style={{ background: blog.topic.bg, color: blog.topic.fg }}>
                      {blog.topic.label}
                    </span>
                    <span className="read-meta">{blog.mins} min read</span>
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 400, lineHeight: 1.25, margin: '0 0 10px', color: 'var(--text-on-light)' }}>
                    {blog.title}
                  </h3>
                  <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 13, lineHeight: 1.6, opacity: 0.6, color: 'var(--text-on-light)', margin: '0 0 auto' }}>
                    {blog.excerpt}
                  </p>
                  <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 12, opacity: 0.4, color: 'var(--text-on-light)', margin: '14px 0 0' }}>
                    {new Date(blog.lastmod).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {' · '}By Niki Charnock
                  </p>
                </div>
              </Link>
            ))
          )}
        </div>

        {/* Email capture footer */}
        <div style={{ marginTop: 72, background: 'var(--bg-surface-dk)', border: '1px solid var(--border-on-light)', borderRadius: 20, padding: '44px 40px', textAlign: 'center' }}>
          <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', opacity: 0.45, marginBottom: 12 }}>
            Stay informed
          </p>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: 300, margin: '0 0 14px', lineHeight: 1.15 }}>
            Wellness insight, straight to your inbox
          </h2>
          <p style={{ fontFamily: "var(--font-dm-sans)", fontSize: 14, opacity: 0.55, maxWidth: 400, margin: '0 auto 28px', lineHeight: 1.6 }}>
            Latest research, seasonal recipes, and gentle guidance from Niki — no noise, no spam.
          </p>
          <Link
            href="/contactus"
            style={{ display: 'inline-block', fontFamily: "var(--font-dm-sans)", fontSize: 14, fontWeight: 500, background: 'var(--text-brand)', color: 'var(--text-on-dark)', padding: '12px 32px', borderRadius: 10, textDecoration: 'none', transition: 'opacity 0.2s' }}
          >
            Get in Touch →
          </Link>
        </div>

      </div>
    </main>
  );
}
